export class ApiError extends Error {
  status: number | null;
  code: string | null;
  serverVersion?: number;

  constructor(message: string, details: { status: number | null; code: string | null; serverVersion?: number }) {
    super(message);
    this.status = details.status;
    this.code = details.code;
    this.serverVersion = details.serverVersion;
  }
}

export type ApiDeps = {
  fetch: typeof fetch;
  apiBase: string;
  timeoutMs: number;
  sleep: (ms: number) => Promise<void>;
  random: () => number;
};

export function defaultDeps(apiBase: string): ApiDeps {
  return {
    fetch: (...args) => globalThis.fetch(...args),
    apiBase,
    timeoutMs: 10_000,
    sleep: ms => new Promise(resolve => setTimeout(resolve, ms)),
    random: Math.random,
  };
}

export async function apiFetch(path: string, opts: RequestInit, token: string | null, deps: ApiDeps): Promise<any> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(opts.headers as Record<string, string>),
  };
  if (token) headers['Authorization'] = `Bearer ${token}`;

  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), deps.timeoutMs);

  let resp: Response;
  try {
    resp = await deps.fetch(`${deps.apiBase}${path}`, { ...opts, headers, signal: controller.signal });
  } catch (e: any) {
    clearTimeout(timeoutId);
    if (e.name === 'AbortError') {
      throw new ApiError('請求逾時，請檢查網路連線', { status: null, code: 'TIMEOUT' });
    }
    throw e;
  }
  try {
    if (resp.status === 409) {
      const body = await resp.json().catch(() => null);
      throw new ApiError(body?.error || 'Conflict', {
        status: 409, code: 'CONFLICT', serverVersion: body?.server_version,
      });
    }
    if (resp.status === 401) {
      const body = await resp.json().catch(() => null);
      const code = typeof body?.code === 'string' && body.code.length > 0 ? body.code : 'INVALID_TOKEN';
      throw new ApiError(body?.error || 'HTTP 401', { status: 401, code });
    }
    if (!resp.ok) {
      const body = await resp.json().catch(() => null);
      throw new ApiError(body?.error || `HTTP ${resp.status}`, {
        status: resp.status, code: typeof body?.code === 'string' ? body.code : null,
      });
    }
    try {
      return await resp.json();
    } catch (e: any) {
      if (e?.name === 'AbortError') {
        throw new ApiError('請求逾時，請檢查網路連線', { status: null, code: 'TIMEOUT' });
      }
      throw e;
    }
  } finally {
    clearTimeout(timeoutId);
  }
}

export function isRetryable(e: unknown): boolean {
  return !(e instanceof ApiError && e.status !== null && e.status >= 400 && e.status < 500);
}

export function shouldClearToken(e: unknown): boolean {
  return e instanceof ApiError && e.status === 401;
}

export async function settleAuthFirst(promises: Promise<any>[]): Promise<any[]> {
  const results = await Promise.allSettled(promises);
  const authError = results.find(result => result.status === 'rejected' && shouldClearToken(result.reason));
  if (authError?.status === 'rejected') throw authError.reason;
  return results.map(result => {
    if (result.status === 'rejected') throw result.reason;
    return result.value;
  });
}

export function isTokenUpgradeRequired(e: unknown): boolean {
  return shouldClearToken(e) && (e as ApiError).code === 'TOKEN_UPGRADE_REQUIRED';
}

export function shouldClearForUpgrade(e: unknown, tokenAtCall: string | null, tokenNow: string | null): boolean {
  return isTokenUpgradeRequired(e) && tokenAtCall !== null && tokenAtCall === tokenNow;
}

export async function apiRequest(path: string, opts: RequestInit, token: string | null, deps: ApiDeps): Promise<any> {
  const method = (opts.method || 'GET').toUpperCase();
  const maxRetries = method === 'GET' ? 2 : 0;

  for (let attempt = 0; attempt <= maxRetries; attempt++) {
    try {
      return await apiFetch(path, opts, token, deps);
    } catch (e) {
      if (attempt < maxRetries && isRetryable(e)) {
        const backoff = Math.pow(2, attempt) * 1000;
        const jitter = deps.random() * 500;
        await deps.sleep(backoff + jitter);
        continue;
      }
      throw e;
    }
  }
}
